// src/components/GrilleAdjudication.jsx — LA GRILLE D'ADJUDICATION D'UN POSTE
// (ou d'un dossier AO), LUE PAR LE SERVEUR, VALIDÉE PAR UNE PERSONNE.
//
// Expert AO, 30.10 : « écran de validation de la grille détectée (poids et
// mode excluant) à l'import de chaque besoin » ; « si le mot Pondération est
// présent mais non compris, ou si un libellé est inconnu → alerte, jamais de
// retour silencieux à la grille par défaut ». Générique : n'importe quel
// appel d'offres, n'importe quelle formulation — le serveur lit
// (utils/grilleAo), l'écran montre, corrige, valide.
//
// Trois morceaux :
//  - GrilleAdjudication : l'éditeur (parts en %, total, mode excluant, source,
//    alertes, texte lu) — réutilisable ;
//  - GrilleBesoin : le résumé compact sur la fiche de poste d'un besoin, qui
//    ouvre l'éditeur (GET / PUT /besoins/:id/grille) ;
//  - GrilleAo : la carte « Grille par défaut (CCCC) » d'un dossier AO
//    (GET / PUT /ao/:id/grille, POST /ao/:id/grille/lire, rejeu sans IA).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import APIService from '../services/api';

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, className }) => <i className={`bi bi-${icon}${className ? ` ${className}` : ''}`} aria-hidden="true" />;
const authH = () => ({ Authorization: `Bearer ${APIService.getToken()}` });
const jsonH = () => ({ ...authH(), 'Content-Type': 'application/json' });
const absente = (r, j) => [404, 405, 501].includes(r.status) && (!j?.error || /route non trouv/i.test(j.error));
const ABSENTE = 'La grille arrive côté serveur.';
const fmtDate = (d) => { const t = d ? new Date(d) : null; return t && !isNaN(t) ? t.toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit', year: 'numeric' }) : null; };

// Les parts du moteur, dans l'ordre où une grille d'appel d'offres les écrit.
// « Compétences requises » réunit indispensables et importantes, notées
// ensemble sur leur part : elle n'apparaît que si la grille la porte (ou
// qu'on l'ajoute).
export const PARTS = [
  { k: 'primordiales', libelle: 'Indispensables' },
  { k: 'importantes', libelle: 'Importantes' },
  { k: 'competences', libelle: 'Compétences requises', aide: 'indispensables et importantes réunies, notées ensemble', facultative: true },
  { k: 'optionnelles', libelle: 'Optionnelles' },
  { k: 'formation', libelle: 'Formation' },
  { k: 'role', libelle: 'Adéquation au rôle, activités et qualités' },
];

/** D'où vient la grille, en mots. */
export function sourceEnMots(source, valideLe) {
  const le = fmtDate(valideLe);
  switch (source) {
    case 'fiche_validee': return `Validée${le ? ` le ${le}` : ''}`;
    case 'fiche': return 'Lue dans la fiche du poste — à valider';
    case 'ao_validee': return 'Grille par défaut du dossier (CCCC), validée';
    case 'ao': return 'Grille par défaut du dossier (CCCC) — à valider';
    case 'cccc': return le ? `Lue dans le CCCC, validée le ${le}` : 'Lue dans le CCCC — à valider';
    case 'saisie': return le ? `Saisie à la main, validée le ${le}` : 'Saisie à la main';
    default: return 'Grille codée par défaut';
  }
}

const enPourcents = (grille) => Object.fromEntries(PARTS.map(p => [p.k, grille && Number(grille[p.k]) > 0
  ? String(Math.round(Number(grille[p.k]) * 1000) / 10) : '']));

/** La barre des 100 points : une part, un segment. */
function Barre({ valeurs }) {
  const parts = PARTS.map(p => ({ ...p, v: Number(String(valeurs[p.k] || '').replace(',', '.')) || 0 })).filter(p => p.v > 0);
  const total = parts.reduce((s, p) => s + p.v, 0);
  if (!total) return <div className="grille-barre" aria-hidden="true" />;
  return (
    <div className="grille-barre" role="img" aria-label={parts.map(p => `${p.libelle} ${Math.round(p.v)} %`).join(', ')}>
      {parts.map(p => (
        <span key={p.k} className={`grille-barre__part grille-barre__part--${p.k}`} style={{ width: `${(p.v / total) * 100}%` }}
          title={`${p.libelle} : ${Math.round(p.v * 10) / 10} %`} />
      ))}
    </div>
  );
}

/**
 * L'ÉDITEUR. `grille` en fractions (servie par le serveur) ; `onValider`
 * reçoit { grille (en %), excluantes } et rend une promesse.
 */
export function GrilleAdjudication({ titre = 'Grille d’adjudication', grille, excluantes = false, source = null, valideLe = null,
  alertes = [], inconnus = [], adjudication = null, avecExcluantes = true, busy = false, onValider, onFermer }) {
  const [valeurs, setValeurs] = useState(() => enPourcents(grille));
  const [excl, setExcl] = useState(!!excluantes);
  const [avecCompetences, setAvecCompetences] = useState(Number(grille?.competences) > 0);
  useEffect(() => { setValeurs(enPourcents(grille)); setAvecCompetences(Number(grille?.competences) > 0); }, [grille]);
  useEffect(() => { setExcl(!!excluantes); }, [excluantes]);

  const visibles = PARTS.filter(p => !p.facultative || avecCompetences);
  const total = useMemo(() => visibles.reduce((s, p) => s + (Number(String(valeurs[p.k] || '').replace(',', '.')) || 0), 0), [valeurs, visibles]);
  const ecart = Math.abs(total - 100) > 0.5;
  const valides = visibles.filter(p => (Number(String(valeurs[p.k] || '').replace(',', '.')) || 0) > 0).length;
  const sansInconnus = (inconnus || []).filter(x => !(alertes || []).some(a => a.includes(x)));

  const valider = () => {
    const g = {};
    for (const p of visibles) {
      const n = Number(String(valeurs[p.k] || '').replace(',', '.'));
      if (Number.isFinite(n) && n > 0) g[p.k] = n;
    }
    onValider?.({ grille: g, excluantes: excl });
  };

  return (
    <section className="grille" aria-label={titre}>
      <header className="grille__tete">
        <h3 className="grille__titre">{titre}</h3>
        <span className="grille__source">{sourceEnMots(source, valideLe)}</span>
        {onFermer && <button type="button" className="grille__fermer" onClick={onFermer} aria-label="Fermer la grille"><BI icon="x-lg" /></button>}
      </header>

      {/* Ce qui n'a pas été compris passe AVANT les chiffres. */}
      {((alertes || []).length > 0 || sansInconnus.length > 0) && (
        <div className="grille-alerte" role="alert">
          <BI icon="exclamation-triangle" />
          <div>
            {(alertes || []).map((a, i) => <p key={i}>{a}</p>)}
            {sansInconnus.length > 0 && <p>Libellés non reconnus : {sansInconnus.map(x => `« ${x} »`).join(', ')}.</p>}
          </div>
        </div>
      )}

      <Barre valeurs={Object.fromEntries(visibles.map(p => [p.k, valeurs[p.k]]))} />

      <div className="grille-lignes">
        {visibles.map(p => (
          <React.Fragment key={p.k}>
            <label className="grille-ligne__lib" htmlFor={`grille-${p.k}`}>
              <span className={`grille-ligne__pastille grille-barre__part--${p.k}`} aria-hidden="true" />
              {p.libelle}
              {p.aide && <span className="grille-ligne__aide">{p.aide}</span>}
            </label>
            <span className="grille-ligne__champ">
              <input id={`grille-${p.k}`} className="erp-field grille-ligne__input" inputMode="decimal" value={valeurs[p.k]}
                disabled={busy} placeholder="0" aria-describedby="grille-total"
                onChange={e => setValeurs(v => ({ ...v, [p.k]: e.target.value.replace(/[^\d.,]/g, '') }))} />
              <span aria-hidden="true">%</span>
            </span>
          </React.Fragment>
        ))}
      </div>
      {!avecCompetences && (
        <button type="button" className="grille__lien" onClick={() => setAvecCompetences(true)} disabled={busy}>
          <BI icon="plus-lg" /> Ajouter « Compétences requises » (indispensables et importantes réunies)
        </button>
      )}
      <div id="grille-total" className={`grille-total${ecart ? ' grille-total--ecart' : ''}`}>
        <span>Total</span>
        <span>{Math.round(total * 10) / 10} %{ecart ? ' — les parts seront ramenées à 100' : ''}</span>
      </div>

      {avecExcluantes && (
        <label className="grille-bascule">
          <input type="checkbox" checked={excl} disabled={busy} onChange={e => setExcl(e.target.checked)} />
          <span>
            <strong>Indispensables excluantes</strong>
            <span className="grille-ligne__aide">Une seule indispensable manquante élimine le profil. Sinon, elle fait perdre le bloc des indispensables (tout ou rien).</span>
          </span>
        </label>
      )}

      {adjudication && (
        <details className="grille-texte">
          <summary>Texte lu dans le document</summary>
          <pre>{adjudication}</pre>
        </details>
      )}

      <div className="grille__pied">
        {onFermer && <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={onFermer} disabled={busy}>Fermer</button>}
        <button type="button" className="erp-btn erp-btn--primary erp-btn--sm" onClick={valider} disabled={busy || valides < 2}
          title={valides < 2 ? 'Une grille compte au moins deux parts' : undefined}>
          <BI icon={busy ? 'arrow-repeat' : 'check2'} className={busy ? 'grille-tourne' : undefined} /> {busy ? 'Validation…' : 'Valider la grille'}
        </button>
      </div>
    </section>
  );
}

/**
 * LE RÉSUMÉ SUR LA FICHE DE POSTE D'UN BESOIN. Rien ne s'affiche tant que le
 * besoin n'a ni grille, ni alerte (poste hors appel d'offres) — ni si la
 * route n'existe pas encore. `ouvrir` (après un import) déplie l'éditeur.
 */
export function GrilleBesoin({ besoinId, showNotification, ouvrir = 0, titre = 'Grille d’adjudication' }) {
  const [etat, setEtat] = useState(null);   // null = lecture, false = absente
  const [ouvert, setOuvert] = useState(false);
  const [busy, setBusy] = useState(false);
  const charger = useCallback(async () => {
    if (besoinId == null) return;
    try {
      const r = await fetch(`${BASE}/besoins/${besoinId}/grille`, { headers: authH() });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) { setEtat(false); return; }
      setEtat(j);
    } catch { setEtat(false); }
  }, [besoinId]);
  useEffect(() => { charger(); }, [charger]);
  useEffect(() => { if (ouvrir) { charger(); setOuvert(true); } }, [ouvrir]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!etat) return null;
  const a = etat.applique || {};
  const lue = etat.lue || null;
  const alertes = a.alertes || [];
  if (!a.grille && !alertes.length && !lue && !etat.adjudication) return null;

  const valider = async ({ grille, excluantes }) => {
    setBusy(true);
    try {
      const r = await fetch(`${BASE}/besoins/${besoinId}/grille`, { method: 'PUT', headers: jsonH(), body: JSON.stringify({ grille, excluantes }) });
      const j = await r.json().catch(() => ({}));
      if (absente(r, j)) throw new Error(ABSENTE);
      if (!r.ok || j.success === false) throw new Error(j.error || `Refusé (${r.status}).`);
      showNotification?.(j.message, 'success');
      setOuvert(false);
      await charger();
    } catch (e) { showNotification?.(e.message, 'error'); }
    finally { setBusy(false); }
  };

  const aValider = !['fiche_validee'].includes(a.source) && (a.source === 'fiche' || !!lue);
  return (
    <div className="grille-zone">
      <div className="grille-resume">
        <BI icon="sliders" />
        <span className="grille-resume__phrase">
          <strong>{titre}</strong> · {a.phrase || 'grille codée par défaut'}
          {a.excluantes ? ' · indispensables excluantes' : ''}
          <span className="grille__source"> — {sourceEnMots(a.source, lue?.valide_le)}</span>
        </span>
        {alertes.length > 0 && (
          <span className="grille-resume__alerte" title={alertes.join('\n')}>
            <BI icon="exclamation-triangle" /> {alertes.length} point{alertes.length > 1 ? 's' : ''} à vérifier
          </span>
        )}
        <button type="button" className={`erp-btn erp-btn--sm ${aValider || alertes.length ? 'erp-btn--primary' : 'erp-btn--secondary'}`}
          onClick={() => setOuvert(o => !o)} aria-expanded={ouvert}>
          {ouvert ? 'Replier' : (aValider || alertes.length ? 'Vérifier et valider' : 'Voir la grille')}
        </button>
      </div>
      {ouvert && (
        <div className="grille-zone__corps">
          <GrilleAdjudication titre={aValider ? 'Grille d’adjudication détectée — à valider' : titre}
            grille={lue?.grille || a.grille} excluantes={lue ? lue.excluantes : a.excluantes} source={a.source} valideLe={lue?.valide_le}
            alertes={alertes} inconnus={lue?.inconnus || []} adjudication={etat.adjudication} busy={busy}
            onValider={valider} onFermer={() => setOuvert(false)} />
        </div>
      )}
    </div>
  );
}

/**
 * LA CARTE « GRILLE PAR DÉFAUT (CCCC) » D'UN DOSSIER AO : déposer le CCCC,
 * valider la grille lue, rejouer les scores sans IA.
 */
export function GrilleAo({ aoId, showNotification, onRejoue }) {
  const [etat, setEtat] = useState(null);   // null = lecture, false = absente
  const [ouvert, setOuvert] = useState(false);
  const [busy, setBusy] = useState(null);
  const fichierRef = useRef(null);
  const charger = useCallback(async () => {
    if (!aoId) return;
    try {
      const r = await fetch(`${BASE}/ao/${aoId}/grille`, { headers: authH() });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) { setEtat(false); return; }
      setEtat(j);
    } catch { setEtat(false); }
  }, [aoId]);
  useEffect(() => { charger(); }, [charger]);
  if (!etat) return null;
  const g = etat.grille || null;

  const deposer = async (fichier) => {
    if (!fichier) return;
    setBusy('lire');
    try {
      const fd = new FormData();
      fd.append('fichier', fichier);
      const r = await fetch(`${BASE}/ao/${aoId}/grille/lire`, { method: 'POST', headers: authH(), body: fd });
      const j = await r.json().catch(() => ({}));
      if (absente(r, j)) throw new Error(ABSENTE);
      if (!r.ok || j.success === false) throw new Error(j.error || `Le CCCC n’a pas pu être lu (${r.status}).`);
      showNotification?.(j.message, (j.grille?.alertes || []).length ? 'info' : 'success');
      await charger();
      setOuvert(true);
    } catch (e) { showNotification?.(e.message, 'error'); }
    finally { setBusy(null); }
  };
  const valider = async ({ grille }) => {
    setBusy('valider');
    try {
      const r = await fetch(`${BASE}/ao/${aoId}/grille`, { method: 'PUT', headers: jsonH(), body: JSON.stringify({ grille }) });
      const j = await r.json().catch(() => ({}));
      if (absente(r, j)) throw new Error(ABSENTE);
      if (!r.ok || j.success === false) throw new Error(j.error || `Refusé (${r.status}).`);
      showNotification?.(j.message, 'success');
      setOuvert(false);
      await charger();
    } catch (e) { showNotification?.(e.message, 'error'); }
    finally { setBusy(null); }
  };
  const rejouer = async () => {
    setBusy('rejouer');
    try {
      const r = await fetch(`${BASE}/ao/${aoId}/matching/rejouer`, { method: 'POST', headers: jsonH(), body: JSON.stringify({}) });
      const j = await r.json().catch(() => ({}));
      if (absente(r, j)) throw new Error('Le rejeu sans IA arrive côté serveur.');
      if (!r.ok || j.success === false) throw new Error(j.error || `Refusé (${r.status}).`);
      showNotification?.(j.message, 'success');
      onRejoue?.();
    } catch (e) { showNotification?.(e.message, 'error'); }
    finally { setBusy(null); }
  };

  const alertes = g?.alertes || [];
  return (
    <section className="grille-carte" aria-label="Grille par défaut du dossier">
      <input ref={fichierRef} type="file" accept=".pdf,.doc,.docx,.odt,.txt" hidden
        onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; deposer(f); }} />
      <div className="grille-carte__ligne">
        <BI icon="sliders" />
        <strong className="grille-carte__titre">Grille par défaut (CCCC)</strong>
        {g?.grille
          ? <span className="grille-resume__phrase">{etat.phrase} <span className="grille__source">— {sourceEnMots(g.source, g.valide_le)}</span></span>
          : <span className="grille-resume__alerte grille-resume__phrase"><BI icon="exclamation-triangle" /> {etat.alerte || 'Aucune grille lue dans le CCCC : la grille codée par défaut s’applique aux postes sans grille propre.'}</span>}
        {alertes.length > 0 && <span className="grille-resume__alerte" title={alertes.join('\n')}><BI icon="exclamation-triangle" /> {alertes.length} point{alertes.length > 1 ? 's' : ''} à vérifier</span>}
        <span className="grille-carte__actions">
          <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => fichierRef.current?.click()} disabled={!!busy}
            title="Le document des conditions de l’appel d’offres : la grille par défaut y est lue (section de pondération)">
            <BI icon={busy === 'lire' ? 'arrow-repeat' : 'upload'} className={busy === 'lire' ? 'grille-tourne' : undefined} /> {g ? 'Redéposer le CCCC' : 'Déposer le CCCC'}
          </button>
          {g && (
            <button type="button" className={`erp-btn erp-btn--sm ${g.valide_le ? 'erp-btn--secondary' : 'erp-btn--primary'}`} onClick={() => setOuvert(o => !o)} disabled={!!busy} aria-expanded={ouvert}>
              {ouvert ? 'Replier' : (g.valide_le ? 'Voir la grille' : 'Vérifier et valider')}
            </button>
          )}
          <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={rejouer} disabled={!!busy}
            title="Recalcule les scores du dossier sur les évaluations déjà faites (nouvelle grille, barème corrigé) — sans nouvelle analyse, sans coût">
            <BI icon={busy === 'rejouer' ? 'arrow-repeat' : 'arrow-repeat'} className={busy === 'rejouer' ? 'grille-tourne' : undefined} /> Rejouer les scores (sans IA)
          </button>
        </span>
      </div>
      {ouvert && g && (
        <GrilleAdjudication titre="Grille par défaut du dossier" grille={g.grille} source={g.source} valideLe={g.valide_le}
          alertes={alertes} inconnus={g.inconnus || []} avecExcluantes={false} busy={busy === 'valider'}
          onValider={valider} onFermer={() => setOuvert(false)} />
      )}
    </section>
  );
}

export default GrilleAdjudication;
